import {
	AdSuperGridListDocument,
	CampaignSuperGridListDocument,
	GroupSuperGridListDocument,
	NotificationEntityType,
	RedditNotificationsQueryClient,
	RedditNotificationsQueryItems,
} from 'wb-reddit/graphql/generated';
import { RedditLevel } from 'wb-reddit/app/routes/superGrid/utils';
import { LEVEL_ID_FILTER } from 'wb-reddit/app/routes/superGrid/actionRow/filters/utils';
import { ApolloClient } from '@apollo/client';
import { REDDIT_SUPERGRID_CONFIG, transformRedditRows } from 'wb-reddit/app/routes/superGrid/config';
import {
	getLevelByDepth, getLevelDepth, flattenObject, addSupergridPropsToData,
} from '@aleph/supergrid';
import { GridApi } from 'ag-grid-community';

export type RedditNotification = (RedditNotificationsQueryItems & { validationErrors?: Record<string, { message: string }> });

export type ClientGroupedNotifications = {
	client?: RedditNotificationsQueryClient | null,
	notifications: RedditNotification[]
	occurredAt: RedditNotificationsQueryItems['notificationCreatedAt']
};

const getEntityLevelInfoFromNotification = (notification: RedditNotification) => {
	switch (notification.notificationEntityType) {
		case NotificationEntityType.AdAccount:
			return {
				filter: LEVEL_ID_FILTER[RedditLevel.AD_ACCOUNT],
				level:  RedditLevel.AD_ACCOUNT,
			};
		case NotificationEntityType.Campaign:
			return {
				filter: LEVEL_ID_FILTER[RedditLevel.CAMPAIGN],
				level:  RedditLevel.CAMPAIGN,
			};
		case NotificationEntityType.Group:
			return {
				filter: LEVEL_ID_FILTER[RedditLevel.GROUP],
				level:  RedditLevel.GROUP,
			};
		case NotificationEntityType.Ad:
			return {
				filter: LEVEL_ID_FILTER[RedditLevel.AD],
				level:  RedditLevel.AD,
			};
		case NotificationEntityType.Creative:
			return {
				filter: 'creativeId',
				level:  RedditLevel.AD,
			};
	}

	return {
		filter: undefined,
		level:  undefined,
	};
};

export const getUrlParamsAndValidationsFromNotification = (notification: RedditNotification) => {
	if (notification.notificationEntityId) {
		const { filter: notificationEntityFilter, level: notificationEntityLevel } = getEntityLevelInfoFromNotification(notification);

		if (notificationEntityFilter && notificationEntityLevel) {
			return {
				urlParams: {
					level:      notificationEntityLevel,
					goToColumn: { col: 'settingsElastic' },
					filters:    {
						[notificationEntityFilter]: [{ value: [notification.notificationEntityId] }],
					},
				},
				validations: Object.keys(notification.validationErrors ?? {}).map((field) => ({
					field,
					id:      notification.notificationEntityId as string,
					message: notification.validationErrors?.[field]?.message ?? 'Unknown error',
					level:   notification.notificationEntityType ?? RedditLevel.AD_ACCOUNT as RedditLevel,
				})),
			};
		}
	}

	return {
		urlParams:   undefined,
		validations: [],
	};
};

export const updateEntityFromNotification = async (notifications: RedditNotification[], client: ApolloClient<any>, gridApi: GridApi) => {
	const campaignIds = notifications.filter(notification => notification.notificationEntityType === 'CAMPAIGN').map(notification => notification.notificationEntityId);
	const groupIds = notifications.filter(notification => notification.notificationEntityType === 'GROUP').map(notification => notification.notificationEntityId);
	const adIds = notifications.filter(notification => notification.notificationEntityType === 'AD').map(notification => notification.notificationEntityId);

	const { data: campaignData } = campaignIds?.length > 0 ? await client.query({
		query:     CampaignSuperGridListDocument,
		variables: {
			filters: {
				[LEVEL_ID_FILTER[RedditLevel.CAMPAIGN]]: [{ value: campaignIds }],
			},
			limit: 200, // max notifs
		},
		fetchPolicy: 'no-cache',
	}) : { data: undefined };

	const { data: groupData } = groupIds?.length > 0 ? await client.query({
		query:     GroupSuperGridListDocument,
		variables: {
			filters: {
				[LEVEL_ID_FILTER[RedditLevel.GROUP]]: [{ value: groupIds }],
			},
			limit: 200, // max notifs
		},
		fetchPolicy: 'no-cache',
	}) : { data: undefined };

	const { data: adData } = adIds?.length > 0 ? await client.query({
		query:     AdSuperGridListDocument,
		variables: {
			filters: {
				[LEVEL_ID_FILTER[RedditLevel.AD]]: [{ value: adIds }],
			},
			limit: 200, // max notifs
		},
		fetchPolicy: 'no-cache',
	}) : { data: undefined };

	const campaignUpdates = await transformRedditRows(addSupergridPropsToData(campaignData?.entities?.items ?? [], RedditLevel.CAMPAIGN), RedditLevel.CAMPAIGN);
	const groupUpdates = await transformRedditRows(addSupergridPropsToData(groupData?.entities?.items ?? [], RedditLevel.GROUP), RedditLevel.GROUP);
	const adUpdates = await transformRedditRows(addSupergridPropsToData(adData?.entities?.items ?? [], RedditLevel.AD), RedditLevel.AD);

	// @ts-ignore: accessing private property
	const topLevel = gridApi?.gos?.gridOptions?.context?.gridTopLevel as RedditLevel;

	const entityUpdates = [
		...campaignUpdates,
		...groupUpdates,
		...adUpdates,
	];

	entityUpdates.forEach(update => {
		const route: string[] = [];
		let currLevel = update?.level;
		let currParent = update?.parent;

		while (
			currLevel
			&& currParent
			&& getLevelDepth(REDDIT_SUPERGRID_CONFIG, currLevel) > getLevelDepth(REDDIT_SUPERGRID_CONFIG, topLevel)
		) {
			route.push(currParent?.id);

			currLevel = getLevelByDepth(
				REDDIT_SUPERGRID_CONFIG,
				getLevelDepth(REDDIT_SUPERGRID_CONFIG, currLevel) - 1,
			);
			currParent = currParent?.parent;
		}

		gridApi?.applyServerSideTransaction({
			route:  route.reverse(),
			update: [flattenObject(update)],
		});
	});

	gridApi.refreshCells({ force: true });
};
